import { useMemo } from 'react'
import { AddButton } from './index'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { selectTripAir } from 'slices/tripAirSlice'
import { selectTripSea } from 'slices/tripseaSlice'
import { useBatchOCRMultiThread } from 'api'
import { setLoading, setLoadingMessage } from 'slices/loaderSlice'
import { selectIsLoading, selectLoadingMessage } from 'slices/loaderSlice'
import { Backdrop } from '@mui/material'
import { CircularProgress } from '@mui/material'
import styled from '@emotion/styled'
import { setCurrentTab } from 'slices/customerSteps'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'lib'
import { buildCardCheckOverrides } from 'utils'
import { setTravelModal, selectIsCategorizing } from 'slices/global'
import { useLogActivity } from 'api'
import moment from 'moment'
import { api } from 'lib/axios'
import { getUserId } from 'lib/auth/tokenStorage'
import { incrementImageRefreshCounter } from 'slices/customer'
import { useAutoStatusChange } from 'hooks/useAutoStatusChange'

const prHasDocuments = pr =>
  Boolean(
    pr?.files?.pos_receipt?.length > 0 ||
    pr?.files?.sat_invoice?.length > 0 ||
    pr?.files?.purchase_stamp?.some(group => group?.length > 0) ||
    pr?.purchase_others?.some(group => group?.length > 0) ||
    pr?.payment_methods?.some(pm => pm?.files?.slip?.length > 0)
  )

const BatchOCR = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { checkAndUpdateStatus } = useAutoStatusChange()
  const { handleBatchOCRMultiThread, isBatchOCRMultiThreadDataLoading } =
    useBatchOCRMultiThread()
  const { logApiActivity } = useLogActivity()
  const isLoading = useSelector(selectIsLoading)
  const loadingMessage = useSelector(selectLoadingMessage)
  const isCategorizing = useSelector(selectIsCategorizing)
  const { id } = useParams()
  const tripAir = useSelector(selectTripAir)
  const tripSea = useSelector(selectTripSea)
  const activeTripType =
    tripAir?.attributes?.air_sea_type || tripSea?.air_sea_type
  const passport = useSelector(state => state?.passport)
  const purchase_cards = useSelector(state => state?.paymentCards?.paymentCards)
  const purchase_requests = useSelector(state => state?.purchaseReceipt?.pr)
  const constructBatchOCRPayload = () => {
    const documents = []

    // Add passport document
    if (passport?.id && passport?.files?.length > 0) {
      const passportDocuments = passport.files
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (passportDocuments.length > 0) {
        documents.push({
          type: 'passport',
          document_id:
            passportDocuments.length === 1
              ? passportDocuments[0]
              : passportDocuments,
          record_id: passport.id
        })
      }
    }

    // Add cruise_id document (from trip)
    const activeTrip = activeTripType === 'air' ? tripAir : tripSea
    const tripId =
      activeTripType === 'air' ? tripAir?.attributes?.id : tripSea?.id

    if (tripId && activeTrip?.files?.cruise?.length > 0) {
      const cruiseDocuments = activeTrip.files.cruise
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (cruiseDocuments.length > 0) {
        documents.push({
          type: 'cruise_id',
          document_id:
            cruiseDocuments.length === 1 ? cruiseDocuments[0] : cruiseDocuments,
          record_id: tripId
        })
      }
    }

    // Add boarding_pass document (from trip air)
    if (
      activeTripType === 'air' &&
      tripId &&
      tripAir?.files?.boarding_pass?.length > 0
    ) {
      const boardingPassDocuments = tripAir.files.boarding_pass
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (boardingPassDocuments.length > 0) {
        documents.push({
          type: 'boarding_pass',
          document_id:
            boardingPassDocuments.length === 1
              ? boardingPassDocuments[0]
              : boardingPassDocuments,
          record_id: tripId
        })
      }
    }

    // Add credit card documents
    if (purchase_cards?.length > 0) {
      purchase_cards.forEach(card => {
        const isOcrDone =
          card?.attributes?.ocr_status === '1'
        if (isOcrDone) {
          return
        }

        if (card?.attributes?.id && card?.files?.length > 0) {
          const cardDocuments = card.files
            .filter(
              file =>
                file?.file_obj?.document_revision_id ||
                file?.document_revision_id
            )
            .map(
              file =>
                file.file_obj?.document_revision_id ||
                file?.document_revision_id
            )

          if (cardDocuments.length > 0) {
            documents.push({
              type: 'credit_card',
              document_id:
                cardDocuments.length === 1 ? cardDocuments[0] : cardDocuments,
              record_id: card.attributes.id
            })
          }
        }
      })
    }

    // Add purchase receipt documents (POS and CC slips)
    if (purchase_requests?.length > 0) {
      purchase_requests.forEach(pr => {
        // Add POS receipts
        if (pr?.files?.pos_receipt?.length > 0) {
          const posDocuments = pr.files.pos_receipt
            .filter(
              file =>
                file?.file_obj?.document_revision_id ||
                file?.document_revision_id
            )
            .map(
              file =>
                file.file_obj?.document_revision_id ||
                file?.document_revision_id
            )

          if (posDocuments.length > 0 && pr?.attributes?.id) {
            documents.push({
              type: 'pos_receipt',
              document_id:
                posDocuments.length === 1 ? posDocuments[0] : posDocuments,
              record_id: pr.attributes.id
            })
          }
        }

        // Add credit card receipt slips from payment methods
        if (pr?.payment_methods?.length > 0) {
          pr.payment_methods.forEach(paymentMethod => {
            if (
              paymentMethod?.files?.slip?.length > 0 &&
              paymentMethod?.attributes?.id
            ) {
              const slipDocuments = paymentMethod.files.slip
                .filter(
                  file =>
                    file?.file_obj?.document_revision_id ||
                    file?.document_revision_id
                )
                .map(
                  file =>
                    file.file_obj?.document_revision_id ||
                    file?.document_revision_id
                )

              if (slipDocuments.length > 0) {
                documents.push({
                  type: 'credit_card_receipt',
                  document_id:
                    slipDocuments.length === 1
                      ? slipDocuments[0]
                      : slipDocuments,
                  record_id: paymentMethod.attributes.id
                })
              }
            }
          })
        }
      })
    }

    return {
      contact_id: id,
      documents: documents
    }
  }
  const hasDocumentsToOCR = useMemo(
    () => constructBatchOCRPayload().documents.length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [passport, tripAir, tripSea, purchase_cards, purchase_requests, id]
  )

  const purchaseCards = purchase_cards || []

  const hasCardFiles = card =>
    Boolean(
      card?.attributes?.id &&
      Array.isArray(card?.files) &&
      card.files.length > 0
    )

  const { hasCreditCardImage, hasPendingCreditCardOCR } = useMemo(() => {
    let hasImage = false
    let hasPendingOCR = false

    for (const card of purchaseCards) {
      if (!hasCardFiles(card)) continue

      hasImage = true

      if (card?.attributes?.ocr_status !== '1') {
        hasPendingOCR = true
        break
      }
    }

    return {
      hasCreditCardImage: hasImage,
      hasPendingCreditCardOCR: hasPendingOCR,
    }
  }, [purchaseCards])

  // A purchase request can only be sent for OCR once its affiliate is picked,
  // so an unselected affiliate on any purchase holding documents blocks OCR.
  const hasMissingAffiliate = useMemo(
    () =>
      (purchase_requests || []).some(
        pr => prHasDocuments(pr) && !pr?.attributes?.affiliate_company_id
      ),
    [purchase_requests]
  )

  const ocrDisabledReason = isCategorizing
    ? 'Categorizing documents...'
    : hasMissingAffiliate
      ? 'Affiliate selection mandatory'
      : !hasDocumentsToOCR
        ? hasCreditCardImage && !hasPendingCreditCardOCR
          ? 'OCR already done.'
          : 'No document available for OCR.'
        : undefined

  const handleBatchOCRClick = async () => {
    if (!hasDocumentsToOCR || isCategorizing || hasMissingAffiliate) return
    dispatch(setLoading(true))
    dispatch(setLoadingMessage('OCR documents processing...'))
    try {
      const payload = constructBatchOCRPayload()
      if (payload.documents.length === 0) {
        console.error(
          'No documents Ids found for batch OCR Multi Thread processing'
        )
        return
      }

      const agentId = getUserId() || ''
      const response = await logApiActivity({
        contactId: id,
        agentId,
        apiEndpoint: '/Api/V8/custom/customer/batch-ocr-process-multithreaded',
        actionType: 'batch_ocr',
        requestPayload: payload,
        apiCall: async () => {
          return await handleBatchOCRMultiThread(payload)
        }
      })

      if (response) {
        const now = moment()
        const utcMoment = now.utc()
        const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

        let body = {
          data: {
            type: 'gb_action_tracking_logs',
            attributes: {
              affiliate_member_id: getUserId(),
              action: 'batch_ocr',
              action_time: formattedTime,
              parent_id: id
            }
          }
        }
        // api.post('/Api/V8/module', body).catch(error => {
        //   console.error('Error saving batch OCR activity:', error)
        // })
        const cardCheckOverrides = buildCardCheckOverrides(response)
        await checkAndUpdateStatus(cardCheckOverrides)
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.CUSTOMER_DETAILS, id]
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.DOCUMENTS, id]
          }),
        ])
        dispatch(incrementImageRefreshCounter())
        dispatch(setLoading(false))
        dispatch(setLoadingMessage('OCR documents processing completed'))
        dispatch(setTravelModal({ show: true, triggeredByOCR: true }))
      }
    } catch (error) {
      console.error('Batch OCR Multi Thread failed:', error)
    } finally {
      dispatch(setLoading(false))
      dispatch(setLoadingMessage(''))
      dispatch(setCurrentTab(1))
    }
  }

  return (
    <>
      <AddButton
        text="OCR"
        onClick={handleBatchOCRClick}
        disabled={
          isBatchOCRMultiThreadDataLoading ||
          !hasDocumentsToOCR ||
          isCategorizing ||
          hasMissingAffiliate
        }
        title={ocrDisabledReason}
        sx={{
          marginRight: '20px'
        }}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <LoadingContainer>
          <CircularProgress color="inherit" />
          {loadingMessage && <LoadingMessage>{loadingMessage}</LoadingMessage>}
        </LoadingContainer>
      </Backdrop>
    </>
  )
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const LoadingMessage = styled.div`
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`

export default BatchOCR
